import React from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface CompoundGrowthChartProps {
    principal: number;
    interestRate: number;
    yearsToRetirement: number;
}

const CompoundGrowthChart: React.FC<CompoundGrowthChartProps> = ({
    principal,
    interestRate,
    yearsToRetirement
}) => {
    // Generate data for compound growth over time
    const generateChartData = () => {
        const years = Array.from({ length: yearsToRetirement + 1 }, (_, i) => i);
        const growthData = years.map(year => {
            // Compound interest formula: FV = P(1 + r)^t
            return principal * Math.pow(1 + interestRate / 100, year);
        });

        return {
            labels: years.map(year => `Year ${year}`),
            datasets: [
                {
                    label: 'Potential Growth',
                    data: growthData,
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    fill: true,
                    tension: 0.3
                }
            ]
        };
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                            }).format(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    maxTicksLimit: 5
                }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => {
                        return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            maximumSignificantDigits: 3
                        }).format(value as number);
                    }
                }
            }
        }
    };

    return (
        <div className="compound-growth-chart">
            <h4>Potential Future Value of ${principal.toFixed(2)}</h4>
            <div className="chart-container" style={{ height: '200px', width: '100%' }}>
                <Line data={generateChartData()} options={options} />
            </div>
            <div className="chart-summary">
                <p>
                    If left invested at {interestRate.toFixed(1)}%, this amount could grow to{' '}
                    <strong>
                        {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD'
                        }).format(principal * Math.pow(1 + interestRate / 100, yearsToRetirement))}
                    </strong>{' '}
                    by retirement. Are you sure you want to proceed?
                </p>
            </div>
        </div>
    );
};

export default CompoundGrowthChart;
